import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { NavLink, useLocation } from "react-router-dom";
import type { NavigationLink } from "./Navbar";
import { CookieSettingsLink } from "@/consent";
import { RESOURCE_LINKS, ResourceLinkLabel } from "./ResourcesMenu";

interface MobileSideMenuProps {
  open: boolean;
  onClose: () => void;
  onPanelMount?: (el: HTMLDivElement | null) => void;
  links: NavigationLink[];
  isAuthenticated: boolean;
}

const MobileSideMenu: React.FC<MobileSideMenuProps> = ({ open, onClose, onPanelMount, links, isAuthenticated }) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const lastLocationRef = useRef(`${location.pathname}${location.search}${location.hash}`);

  useEffect(() => {
    if (!onPanelMount) return;
    onPanelMount(panelRef.current);
    return () => onPanelMount(null);
  }, [onPanelMount]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      // Prevent body scroll when menu open
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
    [
      "block rounded-lg px-4 py-2 text-sm font-medium transition-colors",
      isActive
        ? "bg-brand-secondary/10 text-foreground-light dark:text-foreground-dark"
        : "text-foreground-light hover:bg-brand-secondary/10 hover:text-brand-secondary-light dark:text-foreground-dark dark:hover:text-brand-secondary-light",
    ].join(" ");

  useEffect(() => {
    const current = `${location.pathname}${location.search}${location.hash}`;
    const previous = lastLocationRef.current;
    if (current !== previous) {
      lastLocationRef.current = current;
      if (open) {
        // Delay closing until after navigation effects run
        requestAnimationFrame(() => {
          onClose();
        });
      }
    }
  }, [location, open, onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden={!open}
        className={[
          "fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        ].join(" ")}
        onClick={onClose}
      />

      {/* Side Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!open}
        inert={!open}
        className={[
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-surface-border-light bg-surface-card-light p-4 shadow-xl transition-transform duration-200 ease-out dark:border-surface-border-dark dark:bg-surface-card-dark",
          open ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="mb-4 flex items-center justify-between">
          <span className="px-2 text-xs font-semibold tracking-[0.15em] text-foreground-muted-light dark:text-foreground-muted-dark">MENU</span>
          <Button onClick={onClose} size="icon" variant="ghost" aria-label="Close menu">
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94L6.28 5.22Z" />
            </svg>
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto">
          {links.length > 0 ? (
            <ul className="space-y-1">
              {links.map(({ to, label }) => (
                <li key={to}>
                  <NavLink to={to} prefetch="intent" className={navLinkClassName}>
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-dashed border-surface-border-light bg-surface-card-light p-4 text-sm text-foreground-muted-light dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-foreground-muted-dark">
              {isAuthenticated ? "Navigation links unavailable." : "Sign in to access extensions and admin tools."}
            </div>
          )}
          <section aria-labelledby="mobile-resources-heading" className="mt-6 border-t border-border pt-4">
            <h2 id="mobile-resources-heading" className="px-4 pb-2 text-sm font-semibold text-muted-foreground">
              Resources
            </h2>
            <ul className="space-y-1">
              {RESOURCE_LINKS.map(({ to, label, external }) => (
                <li key={to}>
                  {external ? (
                    <a
                      href={to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ResourceLinkLabel label={label} external />
                    </a>
                  ) : (
                    <NavLink to={to} className={navLinkClassName}>
                      {label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </nav>
        <div className="mt-6 border-t border-surface-border-light pt-4 dark:border-surface-border-dark">
          <CookieSettingsLink size="default" variant="outline" />
        </div>
      </div>
    </>
  );
};

export default MobileSideMenu;
